"use client";

import React, { useState } from "react";
import { Check, ArrowRight, Car, Plane, MapPin, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const services = [{
  id: "airport",
  title: "Airport Transfers",
  description: "Start your vacation the moment you land at Sangster International. Our dedicated driver provides seamless pick-up and drop-off services directly to Paradise Palms Mobay, ensuring a stress-free arrival in Montego Bay. Groups of 4 or less $60, 5+ $110",
  icon: Plane,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/jamaica_airport_shuttle.png",
  features: ["Flight Monitoring", "Personal Greeting", "Fully Air-Conditioned"],
  pricing: [{
    route: "MBJ Airport to Villa",
    price: "FREE",
    type: "Guest Perk"
  }, {
    route: "Villa to MBJ Airport",
    price: "FREE",
    type: "Guest Perk"
  }, {
    route: "Late Night Arrival",
    price: "FREE",
    type: "24/7 Service"
  }, {
    route: "Group Transfer (9 pax)",
    price: "FREE",
    type: "Full Capacity"
  }]
}, {
  id: "beach-shuttle",
  title: "$20 Local Beach Shuttle",
  description: "Experience the turquoise waters of Doctor's Cave or Tropical Bliss without the hassle of parking. Our convenient beach shuttle offers affordable, reliable transport to the island's most beautiful shorelines.",
  icon: Car,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/montego_bay_beach_ride.png",
  features: ["Scheduled Departures", "Beach Gear Space", "Local Driver Tips", "Return Trip Included"],
  pricing: [{
    route: "Doctor's Cave Beach",
    price: "$10",
    type: "Per Person"
  }, {
    route: "Tropical Bliss Beach",
    price: "$10",
    type: "Per Person"
  }, {
    route: "Dead End Beach",
    price: "$10",
    type: "Per Person"
  }, {
    route: "Custom Local Drop",
    price: "$15",
    type: "On Demand"
  }]
}, {
  id: "front-desk",
  title: "24-Hour Front Desk",
  description: "Our team is always on hand to ensure your stay is perfect. From keyless entry support to booking local excursions or handling maintenance requests, we provide around-the-clock peace of mind.",
  icon: Calendar,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/paradise_palms_concierge.png",
  features: ["24/7 Availability", "Activity Bookings", "Emergency Support", "Local Concierge"],
  pricing: [{
    route: "Check-in Support",
    price: "INCLUDED",
    type: "Standard"
  }, {
    route: "Excursion Booking",
    price: "FREE",
    type: "Service"
  }, {
    route: "Maintenance Request",
    price: "24/7",
    type: "Priority"
  }, {
    route: "General Inquiries",
    price: "ALWAYS",
    type: "Unlimited"
  }],
  imageClassName: "absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent z-10 !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612273/img/PHOTO-2026-04-04-16-38-06-e1775349724930.png)] bg-top"
}];
export default function OurExpertise() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="50244" id="island-perks-services" ref={ref} className={cn("dark bg-background text-foreground py-20 relative overflow-hidden transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 text-foreground">
            Island Perks & Services
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl">
            Exclusive guest amenities designed to make your Montego Bay stay effortless, 
            comfortable, and truly unforgettable.
          </p>
        </div>

        <div className="flex flex-col gap-12 md:gap-20">
          {services.map((service, index) => <div key={service.id} data-index={index} className={cn("group relative flex flex-col lg:flex-row items-stretch gap-8 lg:gap-12", index % 2 === 1 ? "lg:flex-row-reverse" : "")}>
              {}
              <div className="w-full lg:w-1/2 relative min-h-[300px] lg:min-h-[450px] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                <div className={`${service.imageClassName || ""} absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent z-10`} />
                <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-1000 group-hover:scale-110" />
                <div className="absolute bottom-8 left-8 z-20 flex items-center gap-4">
                  <div className="p-4 bg-background/40 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20">
                    <service.icon className="w-8 h-8 text-primary" />
                  </div>
                  <span className="text-white font-bold text-xl tracking-wide drop-shadow-lg">
                    {service.title}
                  </span>
                </div>
              </div>

              {}
              <div className="w-full lg:w-1/2 flex flex-col justify-center">
                <Card className="border-none shadow-none bg-transparent p-0">
                  <CardHeader className="px-0 pt-0">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-primary font-mono text-sm font-bold uppercase tracking-widest">
                        Service 0{index + 1}
                      </span>
                      <div className="h-px w-16 bg-primary/30" />
                    </div>
                    <CardTitle className="text-3xl md:text-4xl mb-6 text-foreground font-bold">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-lg md:text-xl leading-relaxed text-muted-foreground">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="px-0 pb-0">
                    <div className="mb-10">
                      <h4 className="text-xs font-bold text-primary mb-5 uppercase tracking-widest">
                        What's Included
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {service.features.map((feature, i) => <Badge key={i} variant="secondary" className="px-4 py-2 text-sm bg-secondary/30 hover:bg-secondary/50 border-white/5 rounded-full backdrop-blur-sm">
                            <Check className="w-4 h-4 mr-2 text-primary" />
                            {feature}
                          </Badge>)}
                      </div>
                    </div>

                    <Dialog>
                      <DialogTrigger asChild>
                        
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px] bg-card border-border rounded-3xl backdrop-blur-2xl">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-3 text-2xl font-bold">
                            <service.icon className="w-6 h-6 text-primary" />
                            {service.title}
                          </DialogTitle>
                          <DialogDescription className="text-base">
                            Transparent pricing and service information for our guests at Paradise Palms.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="mt-6 border rounded-2xl overflow-hidden bg-background/50">
                          <Table>
                            <TableHeader className="bg-muted/80">
                              <TableRow className="hover:bg-transparent border-border">
                                <TableHead className="w-[50%] font-bold text-foreground">
                                  Service / Route
                                </TableHead>
                                <TableHead className="font-bold text-foreground">Type</TableHead>
                                <TableHead className="text-right font-bold text-foreground">
                                  Rate
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {service.pricing.map((item, idx) => <TableRow key={idx} className="border-border hover:bg-muted/40 transition-colors">
                                  <TableCell className="font-medium text-foreground">
                                    {item.route}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground text-sm">
                                    {item.type}
                                  </TableCell>
                                  <TableCell className="text-right font-bold text-primary text-lg">
                                    {item.price}
                                  </TableCell>
                                </TableRow>)}
                            </TableBody>
                          </Table>
                        </div>
                        <div className="mt-6 text-sm text-muted-foreground text-center italic">
                          * Services are provided exclusively for Paradise Palms guests. 
                          Contact the 24hr desk via WhatsApp to schedule your ride.
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}